import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { Checkbox } from 'components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useAddAPITool, useGetToolById, useUpdateAPITool } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { getToolBaseUrl, getToolEditSubject, getToolVisibility, normalizeToolDetails } from './tool-detail-utils';
import { getToolFormInputClassName, getToolFormTextareaClassName } from '../tool-form-styles';
import { APPROVAL_TO_VALUE, HumanApprovalField, toApprovalOption, type HumanApprovalOption } from './human-approval';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { WarningAlert } from 'components/blocks/warning-alert';

type EditToolModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'create' | 'edit';
  tool?: {
    id: string;
    name: string;
    url: string;
    description: string;
    status?: string;
    created_by?: string;
    CreatedBy?: string;
  };
  isBlocksTool?: boolean;
};

type EditToolFormData = {
  toolName: string;
  url: string;
  description: string;
  visibility: ResourceVisibility;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
  status: boolean;
  humanApproval: HumanApprovalOption;
};

const EMPTY_FORM: EditToolFormData = {
  toolName: '',
  url: '',
  description: '',
  visibility: DEFAULT_RESOURCE_VISIBILITY,
  isOpenTool: false,
  isBlocksAuthenticated: false,
  status: false,
  humanApproval: 'auto',
};

export function EditToolModal({
  open,
  onOpenChange,
  mode = 'edit',
  tool,
  isBlocksTool = false,
}: EditToolModalProps) {
  const { t } = useTranslation();
  const isCreate = mode === 'create';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY;
  const { mutateAsync: updateAPITool, isPending: isUpdating } = useUpdateAPITool();
  const { mutateAsync: addAPITool, isPending: isAdding } = useAddAPITool();
  const isPending = isUpdating || isAdding;
  const { data: toolDetails, isLoading: isLoadingToolDetails } = useGetToolById(
    { toolId: tool?.id || '', projectKey },
    { enabled: open && !isCreate && !!tool?.id && !!projectKey }
  );
  // GET /tools/:id returns CreatedBy on the root payload (not only nested tool/data).
  const isOwner = useIsOwner(getToolEditSubject(toolDetails, tool));
  const canEdit = isCreate || isOwner;

  const { register, handleSubmit, formState: { errors }, reset, control, trigger, watch } = useForm<EditToolFormData>({
    mode: 'onChange',
    defaultValues: EMPTY_FORM,
  });

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset(EMPTY_FORM);
    }
    onOpenChange(nextOpen);
  };

  useEffect(() => {
    if (!open) return;

    if (isCreate) {
      reset(EMPTY_FORM);
      return;
    }

    if (!tool?.id) return;

    if (isLoadingToolDetails) return;

    if (toolDetails) {
      const normalized = normalizeToolDetails(toolDetails);
      if (normalized) {
        reset({
          toolName: normalized.name || tool.name || '',
          url: getToolBaseUrl(normalized) || tool.url || '',
          description: normalized.description || tool.description || '',
          visibility: getToolVisibility(normalized),
          isOpenTool: normalized.IsOpenTool ?? normalized.is_open_tool ?? isBlocksTool,
          isBlocksAuthenticated: normalized.IsBlocksAuthenticated ?? false,
          status: normalized.status === 'active',
          humanApproval: toApprovalOption(normalized.RequiresApproval),
        });
        return;
      }
    }

    reset({
      toolName: tool.name,
      url: tool.url,
      description: tool.description,
      visibility: DEFAULT_RESOURCE_VISIBILITY,
      isOpenTool: isBlocksTool,
      isBlocksAuthenticated: false,
      status: tool.status === 'active',
      humanApproval: 'auto',
    });
  }, [open, isCreate, tool, toolDetails, isLoadingToolDetails, reset, isBlocksTool]);

  const isBlocksToolChecked = watch('isOpenTool');

  const onSubmit = async (data: EditToolFormData) => {
    if (!canEdit) return;

    if (!projectKey) {
      toast({
        variant: 'destructive',
        title: t('MISSING_PROJECT_KEY') || 'Missing project key',
        description: t('UNABLE_TO_SAVE_TOOL') || 'Unable to save the tool for this tenant.',
      });
      return;
    }

    if (isCreate) {
      try {
        const response = await addAPITool({
          name: data.toolName.trim(),
          description: data.description.trim(),
          base_url: data.url.trim(),
          is_open_tool: isAdmin ? data.isOpenTool : false,
          is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : false,
          project_key: projectKey,
          visibility: data.visibility,
          requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
        });

        if (!response.is_success) {
          throw new Error(response.message || response.detail || t('FAILED_TO_CREATE_TOOL') || 'Failed to create tool');
        }

        toast({
          variant: 'success',
          title: t('TOOL_CREATED') || 'Tool created',
          description: response.detail || t('API_TOOL_CREATED_SUCCESSFULLY') || 'API tool created successfully.',
        });
        await queryClient.invalidateQueries({ queryKey: ['tools'] });
        handleClose(false);
      } catch (error) {
        toast({
          variant: 'destructive',
          title: t('CREATE_FAILED') || 'Create failed',
          description: error instanceof Error ? error.message : t('FAILED_TO_CREATE_API_TOOL') || 'Failed to create API tool.',
        });
      }
      return;
    }

    if (!tool?.id) return;

    try {
      const response = await updateAPITool({
        id: tool.id,
        name: data.toolName.trim(),
        description: data.description.trim(),
        base_url: data.url.trim(),
        project_key: projectKey,
        visibility: data.visibility,
        is_open_tool: isAdmin ? data.isOpenTool : undefined,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : undefined,
        status: isAdmin && data.isOpenTool ? (data.status ? 'active' : 'inactive') : undefined,
        requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
      });

      if (!response.is_success) {
        throw new Error(response.message || response.detail || t('FAILED_TO_UPDATE_TOOL') || 'Failed to update tool');
      }

      toast({
        variant: 'success',
        title: t('TOOL_UPDATED'),
        description: t('TOOL_UPDATED_SUCCESSFULLY'),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'detail', tool.id, projectKey] }),
      ]);
      handleClose(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('UPDATE_FAILED') || 'Update failed',
        description: error instanceof Error ? error.message : t('FAILED_TO_UPDATE_TOOL') || 'Failed to update tool.',
      });
    }
  };

  const dialogTitle = isCreate
    ? t('ADD_TOOL') || 'Add tool'
    : canEdit
      ? t('EDIT_TOOL') || 'Edit tool'
      : t('VIEW_TOOL') || 'View tool';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-[640px] flex-col overflow-hidden rounded-lg border-0 p-4 shadow-xl sm:p-5">
        <DialogHeader className="shrink-0 space-y-1 px-0 py-0">
          <DialogTitle className="pr-8 text-lg font-semibold leading-snug sm:text-xl">
            {dialogTitle}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {t('ADD_TOOL_DESCRIPTION') || 'Define how this tool will assist your agent in performing tasks.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-0 py-4 pr-1">
            {!isCreate && isLoadingToolDetails ? (
              <div className="space-y-4" aria-hidden>
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-10 w-40" />
              </div>
            ) : (
              <>
                {!canEdit ? (
                  <WarningAlert>
                    {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_TOOL') ||
                      'You are not the owner of this tool.'}
                  </WarningAlert>
                ) : null}

                <div className="space-y-2">
                  <label htmlFor="toolName" className="text-[13px] font-medium leading-6">
                    {t('TOOL_NAME') || 'Tool Name'} <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="toolName"
                    {...register('toolName', { required: t('TOOL_NAME_REQUIRED') || 'Tool name is required' })}
                    disabled={!canEdit || isPending}
                    className={getToolFormInputClassName(!!errors.toolName)}
                  />
                  {errors.toolName ? <p className="text-[13px] text-destructive">{errors.toolName.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="url" className="text-[13px] font-medium leading-6">
                    {t('BASE_URL') || 'Base URL'} <span className="text-destructive">*</span>
                  </label>
                  <Input
                    id="url"
                    {...register('url', {
                      required: t('BASE_URL_REQUIRED') || 'Base URL is required',
                      validate: (value) =>
                        value.startsWith('http://') || value.startsWith('https://')
                          ? true
                          : t('URL_MUST_START_WITH_HTTP') || 'URL must start with http:// or https://',
                    })}
                    onBlur={() => trigger('url')}
                    disabled={!canEdit || isPending}
                    className={getToolFormInputClassName(!!errors.url)}
                  />
                  {errors.url ? <p className="text-[13px] text-destructive">{errors.url.message}</p> : null}
                </div>

                <div className="space-y-2">
                  <label htmlFor="description" className="text-[13px] font-medium leading-6">
                    {t('DESCRIPTION') || 'Description'} <span className="text-destructive">*</span>
                  </label>
                  <Textarea
                    id="description"
                    rows={4}
                    {...register('description', { required: t('DESCRIPTION_REQUIRED') || 'Description is required' })}
                    disabled={!canEdit || isPending}
                    className={getToolFormTextareaClassName(!!errors.description)}
                  />
                  {errors.description ? <p className="text-[13px] text-destructive">{errors.description.message}</p> : null}
                </div>

                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <VisibilityField
                      value={field.value}
                      onValueChange={field.onChange}
                      idPrefix="edit-tool-visibility"
                      disabled={!canEdit || isPending}
                    />
                  )}
                />

                <HumanApprovalField
                  control={control}
                  name="humanApproval"
                  toolType="api"
                  disabled={!canEdit || isPending}
                />

                {isCreate && isAdmin ? (
                  <div className="space-y-4 pt-1">
                    <div className="flex items-center gap-2">
                      <Controller
                        name="isOpenTool"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={!!field.value}
                            disabled={!canEdit}
                            onCheckedChange={(checked) => field.onChange(!!checked)}
                          />
                        )}
                      />
                      <span className="text-[13px] font-medium leading-6">{t('BLOCKS_TOOL') || 'Blocks Tool'}</span>
                    </div>

                    {isBlocksToolChecked ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <Controller
                            name="isBlocksAuthenticated"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={!!field.value}
                                disabled={!canEdit}
                                onCheckedChange={(checked) => field.onChange(!!checked)}
                              />
                            )}
                          />
                          <span className="text-[13px] font-medium leading-6">
                            {t('ALLOW_TOOL_AUTHENTICATION') || 'Allow Tool Authentication'}
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button" className="cursor-help">
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-[13px] leading-6">
                                  {t('ALLOW_TOOL_AUTHENTICATION_HELP') ||
                                    'If checked, users can copy the tool with its authentication.'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <Switch id="toolVisibility" checked={false} disabled className="cursor-not-allowed" />
                          <label htmlFor="toolVisibility" className="text-[13px] font-medium leading-6">
                            {t('TOOL_VISIBILITY') || 'Tool Visibility'}
                          </label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button" className="cursor-help">
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-[13px] leading-6">
                                  {t('TOOL_VISIBILITY_HELP') || 'Admin can edit this tool later to enable it.'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {!isCreate && isBlocksToolChecked ? (
                  <div className="pointer-events-none flex items-center gap-2">
                    <Controller
                      name="isOpenTool"
                      control={control}
                      render={({ field }) => <Checkbox checked={!!field.value} disabled />}
                    />
                    <span className="text-[13px] text-muted-foreground">{t('BLOCKS_TOOL') || 'Blocks Tool'}</span>
                  </div>
                ) : null}

                {!isCreate && isAdmin && isBlocksToolChecked ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Controller
                        name="isBlocksAuthenticated"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={!!field.value}
                            disabled={!canEdit}
                            onCheckedChange={(checked) => field.onChange(!!checked)}
                          />
                        )}
                      />
                      <span className="text-[13px] font-medium leading-6">
                        {t('ALLOW_TOOL_AUTHENTICATION') || 'Allow Tool Authentication'}
                      </span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger type="button" className="cursor-help">
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-[13px] leading-6">
                              {t('ALLOW_TOOL_AUTHENTICATION_HELP') ||
                                'If checked, users can copy the tool with its authentication.'}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={!!field.value}
                            disabled={!canEdit}
                            onCheckedChange={(checked) => field.onChange(!!checked)}
                          />
                        )}
                      />
                      <span className="text-[13px] font-medium leading-6">{t('TOOL_ACTIVE') || 'Tool Active'}</span>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t border-border/40 px-0 pb-0 pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              className="h-10 w-full sm:h-9 sm:w-auto"
            >
              {t('CANCEL') || 'Cancel'}
            </Button>
            {canEdit ? (
              <Button type="submit" disabled={isPending} className="h-10 w-full sm:h-9 sm:w-auto">
                {isPending
                  ? isCreate
                    ? t('CREATING') || 'Creating...'
                    : t('SAVING') || 'Saving...'
                  : isCreate
                    ? t('CREATE') || 'Create'
                    : t('SAVE') || 'Save'}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
